import { useQuery } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';

type TeamMemberRow = Database['public']['Tables']['team_members']['Row'];
type Role = TeamMemberRow['role'];
type Status = TeamMemberRow['status'];

export interface TeamMemberView {
  id: string;
  user_id: string;
  full_name: string | null;
  role: Role;
  status: Status;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
}

export const ROLE_LABELS: Record<string, string> = {
  owner: 'בעלים',
  admin: 'מנהל',
  sales: 'מכירות',
  marketing: 'שיווק',
  viewer: 'צפייה בלבד',
};

export const STATUS_LABELS: Record<string, string> = {
  invited: 'הוזמן',
  active: 'פעיל',
  suspended: 'מושעה',
};

export const catalogTeamKey = (orgId: string | null) => ['catalog-team', orgId] as const;

async function fetchTeam(orgId: string): Promise<TeamMemberView[]> {
  const { data: members, error } = await supabase
    .from('team_members')
    .select('id, user_id, role, status, invited_at, accepted_at, created_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  if (members.length === 0) return [];

  const userIds = members.map((m) => m.user_id);
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('user_id, full_name')
    .in('user_id', userIds);

  const nameByUserId = (profiles ?? []).reduce<Record<string, string | null>>((acc, p) => {
    acc[p.user_id] = p.full_name;
    return acc;
  }, {});

  return members.map((m) => ({
    id: m.id,
    user_id: m.user_id,
    full_name: nameByUserId[m.user_id] ?? null,
    role: m.role,
    status: m.status,
    invited_at: m.invited_at,
    accepted_at: m.accepted_at,
    created_at: m.created_at,
  }));
}

export function useCatalogTeam(orgId: string | null) {
  return useQuery({
    queryKey: catalogTeamKey(orgId),
    queryFn: () => (orgId ? fetchTeam(orgId) : Promise.resolve<TeamMemberView[]>([])),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}
